import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Receipt, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SectionHeader, EmptyState, StatusBadge, StatCard, fmtDate } from "./ui";

const METHODS = ["Cash", "Check", "Card", "Transfer", "Cashapp", "Zelle", "Invoice", "Apple Pay", "Other"];
const CADENCES = ["Weekly", "Monthly", "Quarterly", "Yearly", "One-time"];
const BILL_STATUSES = ["Active", "Cancelled"];
const EMPTY_PAY = { client_id: "none", description: "", amount: "", amount_paid: "", due_date: "", paid_date: "", method: "none", notes: "" };
const EMPTY_BILL = { name: "", client_id: "none", vendor: "", amount: "", cadence: "Monthly", category: "", next_due: "", status: "Active", notes: "" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Billing() {
  const [tab, setTab] = useState("payments");
  const [payments, setPayments] = useState([]);
  const [bills, setBills] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState(EMPTY_PAY);
  const [payEditId, setPayEditId] = useState(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billForm, setBillForm] = useState(EMPTY_BILL);
  const [billEditId, setBillEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sp, setSp] = useSearchParams();

  const load = useCallback(async () => {
    const [p, b, c] = await Promise.all([api.get("/cc/payments"), api.get("/cc/bills"), api.get("/cc/clients")]);
    setPayments(p.data); setBills(b.data); setClients(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const n = sp.get("new");
    if (n === "payment") { openNewPay(); sp.delete("new"); setSp(sp, { replace: true }); }
    if (n === "bill") { setTab("bills"); openNewBill(); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const setP = (k, v) => setPayForm((f) => ({ ...f, [k]: v }));
  const setB = (k, v) => setBillForm((f) => ({ ...f, [k]: v }));
  const openNewPay = () => { setPayEditId(null); setPayForm(EMPTY_PAY); setPayOpen(true); };
  const openEditPay = (p) => {
    setPayEditId(p.id);
    setPayForm({ ...EMPTY_PAY, ...p, client_id: p.client_id || "none", method: p.method || "none", amount: p.amount ?? "", amount_paid: p.amount_paid ?? "" });
    setPayOpen(true);
  };
  const openNewBill = () => { setBillEditId(null); setBillForm(EMPTY_BILL); setBillOpen(true); };
  const openEditBill = (b) => { setBillEditId(b.id); setBillForm({ ...EMPTY_BILL, ...b, client_id: b.client_id || "none", amount: b.amount ?? "" }); setBillOpen(true); };

  const savePayment = async () => {
    if (!payForm.description.trim()) return toast.error("Description is required");
    setSaving(true);
    try {
      const payload = {
        ...payForm,
        client_id: payForm.client_id === "none" ? null : payForm.client_id,
        method: payForm.method === "none" ? null : payForm.method,
        amount: parseFloat(payForm.amount || 0),
        amount_paid: parseFloat(payForm.amount_paid || 0),
      };
      delete payload.id; delete payload.balance; delete payload.client_name; delete payload.status; delete payload.created_at; delete payload.updated_at;
      if (payEditId) { await api.put(`/cc/payments/${payEditId}`, payload); toast.success("Payment updated"); }
      else { await api.post("/cc/payments", payload); toast.success("Payment logged"); }
      setPayOpen(false); setPayForm(EMPTY_PAY); setPayEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const saveBill = async () => {
    if (!billForm.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...billForm, client_id: billForm.client_id === "none" ? null : billForm.client_id, amount: parseFloat(billForm.amount || 0) };
      delete payload.id; delete payload.client_name; delete payload.created_at; delete payload.updated_at;
      if (billEditId) { await api.put(`/cc/bills/${billEditId}`, payload); toast.success("Bill updated"); }
      else { await api.post("/cc/bills", payload); toast.success("Bill added"); }
      setBillOpen(false); setBillForm(EMPTY_BILL); setBillEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const delPayment = async (p) => { if (!window.confirm("Delete this payment record?")) return; try { await api.delete(`/cc/payments/${p.id}`); setPayments((r) => r.filter((x) => x.id !== p.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const delBill = async (b) => { if (!window.confirm("Delete this bill?")) return; try { await api.delete(`/cc/bills/${b.id}`); setBills((r) => r.filter((x) => x.id !== b.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };

  const outstanding = payments.filter((p) => p.status !== "Paid").reduce((s, p) => s + Number(p.balance ?? p.amount ?? 0), 0);
  const collected = payments.reduce((s, p) => s + Number(p.amount_paid || 0), 0);
  const CAD = { Weekly: 52 / 12, Monthly: 1, Quarterly: 1 / 3, Yearly: 1 / 12 };
  const monthlyBills = bills.filter((b) => b.status !== "Cancelled" && b.cadence !== "One-time").reduce((s, b) => s + Number(b.amount || 0) * (CAD[b.cadence] || 1), 0);

  return (
    <div data-testid="billing-page">
      <SectionHeader title="Billing" subtitle="Track client invoices and payments (with deposits), plus your own recurring bills and subscriptions."
        action={tab === "payments" ?
          <Button data-testid="new-payment-button" onClick={openNewPay} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> Log Payment</Button> :
          <Button data-testid="new-bill-button" onClick={openNewBill} className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> Add Bill</Button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-outstanding" label="Outstanding" value={money(outstanding)} accent={outstanding > 0} />
        <StatCard testid="stat-collected" label="Collected" value={money(collected)} />
        <StatCard testid="stat-overdue" label="Overdue" value={payments.filter((p) => p.status === "Overdue").length} />
        <StatCard testid="stat-monthly-bills" label="Bills / month" value={money(monthlyBills)} />
      </div>

      <div className="flex gap-2 mb-5">
        <Button size="sm" variant={tab === "payments" ? "default" : "ghost"} onClick={() => setTab("payments")} data-testid="tab-payments" className={tab === "payments" ? "bg-primary text-primary-foreground gap-1" : "gap-1"}><Receipt size={16} /> Payments</Button>
        <Button size="sm" variant={tab === "bills" ? "default" : "ghost"} onClick={() => setTab("bills")} data-testid="tab-bills" className={tab === "bills" ? "bg-primary text-primary-foreground gap-1" : "gap-1"}><ArrowsClockwise size={16} /> Bills &amp; Subscriptions</Button>
      </div>

      {loading ? <div className="text-muted-foreground">Loading…</div> : tab === "payments" ? (
        payments.length === 0 ? <EmptyState icon={Receipt} title="No payments yet" hint="Log an invoice, deposit or payment to start tracking." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="payments-list">
            {payments.map((p) => (
              <div key={p.id} data-testid={`payment-row-${p.id}`} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.description}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[p.client_name, p.method].filter(Boolean).join(" · ") || "No client"} · due {fmtDate(p.due_date)}
                    {Number(p.amount_paid) > 0 && Number(p.balance) > 0 ? ` · paid ${money(p.amount_paid)} · balance ${money(p.balance)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-lg tracking-tighter">{money(p.amount)}</span>
                  <StatusBadge status={p.status === "Paid" ? "Completed" : p.status === "Partial" ? "In Progress" : "Waiting"} />
                  <Button size="icon" variant="ghost" onClick={() => openEditPay(p)} data-testid={`edit-payment-${p.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => delPayment(p)} data-testid={`delete-payment-${p.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        bills.length === 0 ? <EmptyState icon={ArrowsClockwise} title="No bills or subscriptions" hint="Add a recurring bill or subscription you want to keep an eye on." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="bills-list">
            {bills.map((b) => (
              <div key={b.id} data-testid={`bill-row-${b.id}`} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <div className="font-medium truncate">{b.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{[b.vendor, b.client_name, b.category].filter(Boolean).join(" · ") || "—"} · next {fmtDate(b.next_due)}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-lg tracking-tighter">{money(b.amount)}</span>
                  <span className="text-xs text-muted-foreground w-16">{b.cadence}</span>
                  <Button size="icon" variant="ghost" onClick={() => openEditBill(b)} data-testid={`edit-bill-${b.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => delBill(b)} data-testid={`delete-bill-${b.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{payEditId ? "Edit Payment" : "Log Payment"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Record an invoice, deposit, or payment. Status is set automatically from the amounts.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Description *</Label><Input data-testid="payment-description-input" value={payForm.description} onChange={(e) => setP("description", e.target.value)} placeholder="e.g. Business Formation — deposit" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Total amount ($)</Label><Input data-testid="payment-amount-input" type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setP("amount", e.target.value)} /></div>
              <div><Label>Amount paid ($)</Label><Input data-testid="payment-amount-paid-input" type="number" min="0" step="0.01" value={payForm.amount_paid} onChange={(e) => setP("amount_paid", e.target.value)} placeholder="Deposit / partial" /></div>
              <div>
                <Label>Client</Label>
                <Select value={payForm.client_id} onValueChange={(v) => setP("client_id", v)}>
                  <SelectTrigger data-testid="payment-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Method</Label>
                <Select value={payForm.method} onValueChange={(v) => setP("method", v)}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={payForm.due_date || ""} onChange={(e) => setP("due_date", e.target.value)} /></div>
              <div><Label>Paid date</Label><Input type="date" value={payForm.paid_date || ""} onChange={(e) => setP("paid_date", e.target.value)} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={payForm.notes || ""} onChange={(e) => setP("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button data-testid="save-payment-button" disabled={saving} onClick={savePayment} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : payEditId ? "Save changes" : "Save payment"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill dialog */}
      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{billEditId ? "Edit Bill" : "Add Bill / Subscription"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Track a recurring cost so you never miss a renewal.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="bill-name-input" value={billForm.name} onChange={(e) => setB("name", e.target.value)} placeholder="e.g. Google Workspace" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Vendor</Label><Input data-testid="bill-vendor-input" value={billForm.vendor} onChange={(e) => setB("vendor", e.target.value)} /></div>
              <div><Label>Amount ($)</Label><Input data-testid="bill-amount-input" type="number" min="0" step="0.01" value={billForm.amount} onChange={(e) => setB("amount", e.target.value)} /></div>
              <div>
                <Label>Client</Label>
                <Select value={billForm.client_id} onValueChange={(v) => setB("client_id", v)}>
                  <SelectTrigger data-testid="bill-client-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cadence</Label>
                <Select value={billForm.cadence} onValueChange={(v) => setB("cadence", v)}>
                  <SelectTrigger data-testid="bill-cadence-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CADENCES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Category</Label><Input value={billForm.category} onChange={(e) => setB("category", e.target.value)} /></div>
              <div><Label>Next due</Label><Input type="date" value={billForm.next_due || ""} onChange={(e) => setB("next_due", e.target.value)} /></div>
              <div>
                <Label>Status</Label>
                <Select value={billForm.status} onValueChange={(v) => setB("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{BILL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea value={billForm.notes || ""} onChange={(e) => setB("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBillOpen(false)}>Cancel</Button>
            <Button data-testid="save-bill-button" disabled={saving} onClick={saveBill} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : billEditId ? "Save changes" : "Save bill"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
